import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useDeleteTenant } from './hooks/useDeleteTenant'
import type { Tenant } from '@/types/auth'

interface DeleteTenantDialogProps {
  tenant: Tenant | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteTenantDialog({ tenant, open, onOpenChange }: DeleteTenantDialogProps) {
  const { t } = useTranslation()
  const [confirmationText, setConfirmationText] = useState('')
  const { toast } = useToast()
  const deleteTenant = useDeleteTenant()

  const expectedConfirmation = tenant?.name || ''
  const isConfirmed = confirmationText === expectedConfirmation

  const handleDelete = async () => {
    if (!tenant || !isConfirmed) return

    try {
      await deleteTenant.mutateAsync({
        id: tenant.id,
        confirmationName: confirmationText,
      })

      toast({
        title: t('admin.tenants.delete.success'),
        description: t('admin.tenants.delete.successMessage', { name: tenant.name }),
      })

      setConfirmationText('')
      onOpenChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('admin.tenants.delete.error'),
        description: error instanceof Error ? error.message : t('errors.unknown'),
      })
    }
  }

  const handleClose = () => {
    if (!deleteTenant.isPending) {
      setConfirmationText('')
      onOpenChange(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">
            {t('admin.tenants.delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                <Trans
                  i18nKey="admin.tenants.delete.warning"
                  values={{ name: tenant?.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                <li>{t('admin.tenants.delete.dataList.visits')}</li>
                <li>{t('admin.tenants.delete.dataList.hosts')}</li>
                <li>{t('admin.tenants.delete.dataList.locations')}</li>
                <li>{t('admin.tenants.delete.dataList.settings')}</li>
                <li>{t('admin.tenants.delete.dataList.users')}</li>
              </ul>
              <p className="text-sm">
                <Trans
                  i18nKey="admin.tenants.delete.schemaWarning"
                  values={{ schema: tenant?.schema_name }}
                  components={{ code: <code className="bg-muted px-1 py-0.5 rounded" /> }}
                />
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-4">
          <Label htmlFor="confirmation" className="text-sm font-medium">
            <Trans
              i18nKey="admin.tenants.delete.confirmLabel"
              values={{ name: tenant?.name }}
              components={{ strong: <strong /> }}
            />
          </Label>
          <Input
            id="confirmation"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={tenant?.name}
            disabled={deleteTenant.isPending}
            autoComplete="off"
          />
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={deleteTenant.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmed || deleteTenant.isPending}
          >
            {deleteTenant.isPending ? t('common.deleting') : t('admin.tenants.delete.confirmButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
